/**
 * labAccession.js (frontend)
 * --------------------------
 * Mirror of the backend accession util
 * (radiology-information-system-backend/utils/labAccession.js). The lab tech reads
 * the accession off this UI and keys it into the analyzer by hand, so what the two
 * sides format MUST be byte-identical — keep them in sync.
 *
 * Format:  <letter><id><check>   L40213 = lab order 4021, R1232 = referral order 123.
 * The trailing digit is a Damm check digit over the namespace + id (L->1, R->2), so a
 * single mistyped digit or transposition fails to resolve instead of matching another
 * patient's order. Display only needs formatAccession; parseAccession is included for
 * parity and any future client-side validation.
 */

const NAMESPACES = {
  lab:      { letter: 'L', digit: 1 },
  referral: { letter: 'R', digit: 2 },
};
const BY_LETTER = { L: 'lab', R: 'referral' };

const DAMM = [
  [0, 3, 1, 7, 5, 9, 8, 6, 4, 2],
  [7, 0, 9, 2, 1, 5, 4, 8, 6, 3],
  [4, 2, 0, 6, 8, 7, 1, 3, 5, 9],
  [1, 7, 5, 0, 9, 8, 3, 4, 2, 6],
  [6, 1, 2, 3, 0, 4, 5, 9, 7, 8],
  [3, 6, 7, 4, 2, 0, 9, 5, 8, 1],
  [5, 8, 6, 9, 7, 2, 0, 1, 3, 4],
  [8, 9, 4, 5, 3, 6, 2, 0, 1, 7],
  [9, 4, 3, 8, 6, 1, 7, 2, 0, 5],
  [2, 5, 8, 1, 4, 3, 6, 7, 9, 0],
];

function damm(digits) {
  let interim = 0;
  for (let i = 0; i < digits.length; i++) {
    interim = DAMM[interim][digits.charCodeAt(i) - 48];
  }
  return interim;
}

/**
 * Build the accession string for an order. `type` is 'lab' | 'referral'.
 * Returns null for a missing/invalid id so callers can fall back gracefully.
 */
export function formatAccession(type, id) {
  const ns = NAMESPACES[type];
  if (!ns) return null;
  const n = Number(id);
  if (!Number.isInteger(n) || n <= 0) return null;
  const idStr = String(n);
  return `${ns.letter}${idStr}${damm(String(ns.digit) + idStr)}`;
}

/**
 * Parse an accession string back to { type, id }, or null. Tolerant of case,
 * surrounding whitespace, and left zero-padding — matches the backend exactly.
 */
export function parseAccession(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toUpperCase();
  const m = /^([LR])0*(\d+)$/.exec(s);
  if (!m) return null;
  const type = BY_LETTER[m[1]];
  const body = m[2];
  if (body.length < 2) return null;
  if (damm(String(NAMESPACES[type].digit) + body) !== 0) return null;
  const id = Number(body.slice(0, -1));
  if (!Number.isInteger(id) || id <= 0) return null;
  return { type, id };
}
